import React, { useState, useEffect, useMemo, useContext } from "react";
import GlobalContext from "../../../../../Context/GlobalContext";
import {
  Grid,
  Typography,
  Button,
  Dialog,
  DialogContent,
  CircularProgress,
  Box,
  IconButton,
  Stack,
  Chip,
  Divider,Pagination
} from "@mui/material";
import {
  Close,
  PlayArrowRounded,
  Stars,
  LockPerson,
} from "@mui/icons-material";
//import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const formatRelativeTime = (dateStr) => {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);

  const units = [
    { label: "an", s: 31536000 },
    { label: "mois", s: 2592000 },
    { label: "jour", s: 86400 },
    { label: "heure", s: 3600 },
    { label: "minute", s: 60 },
    { label: "seconde", s: 1 },
  ];

  for (let u of units) {
    const count = Math.floor(diff / u.s);
    if (count >= 1)
      return `il y a ${count} ${u.label}${count > 1 && u.label !== "mois" ? "s" : ""}`;
  }
  return "à l'instant";
};

const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^#&?]{11})/,
  );
  return match ? match[1] : null;
};

const Videos = ({ userData, clientData, onUnlockVideo }) => {
  //const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const [unlockModal, setUnlockModal] = useState({ open: false, video: null });
  const [localUnlockedVideos, setLocalUnlockedVideos] = useState([]);
  const isLoggedIn = !!localStorage.getItem("access_token_story");
  //const unlockedVideos = localUnlockedVideos;
  const [loginModal, setLoginModal] = useState(false);
  const normalizeId = (id) => Number(id);
  const { client } = useContext(GlobalContext) || {};
  const [page, setPage] = useState(1);
  const videosPerPage = 9; // nombre de vidéos par page
  useEffect(() => {
    if (userData) setLoading(false);
  }, [userData]);

  useEffect(() => {
    if (client?.unlocked_videos) {
      setLocalUnlockedVideos(
        client.unlocked_videos.map((v) => normalizeId(v.video_id)),
      );
    }
  }, [client]);

  const unlockedSet = useMemo(() => {
    return new Set(localUnlockedVideos.map(normalizeId));
  }, [localUnlockedVideos]);

  const handleAction = (video) => {
    const videoId = normalizeId(video.id_video_lbrv);
    const isPaid = normalizeId(video.is_paid_video_lbrv) === 1;
    const isFreeVideo = !isPaid;

    const isUnlocked = !isPaid || unlockedSet.has(videoId);

    const ytId = getYouTubeId(video.file_name_lbrv);
    incrementView(video.id_video_lbrv);

    // 🎁 FREE
    if (isFreeVideo) {
      setActiveVideo({
        ...video,
        isYouTube: !!ytId,
        embedUrl: ytId
          ? `https://www.youtube.com/embed/${ytId}?autoplay=1`
          : video.file_name_lbrv,
      });
      return;
    }

    // 🔐 LOGIN
    if (!isLoggedIn) {
      setLoginModal(true);
      return;
    }

    // 💰 LOCKED
    if (!isUnlocked) {
      setUnlockModal({ open: true, video });
      return;
    }

    // ▶️ PLAY
    setActiveVideo({
      ...video,
      isYouTube: !!ytId,
      embedUrl: ytId
        ? `https://www.youtube.com/embed/${ytId}?autoplay=1`
        : video.file_name_lbrv,
    });
  };

  /* API unlock */
  const handleUnlockVideo = async (videoId, tokens) => {
    try {
      const token = localStorage.getItem("access_token_story");

      const res = await fetch(`${process.env.REACT_APP_APIURL}/unlock-video`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          video_id: videoId,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.message || "Erreur lors du déblocage");
        return;
      }

      toast.success("Vidéo débloquée avec succès 🎉");

      // ⚡ UX optimiste (sans localStorage)
      if (onUnlockVideo) {
        onUnlockVideo(videoId, tokens);
      }

      setLocalUnlockedVideos((prev) => {
        const id = normalizeId(videoId);
        if (prev.includes(id)) return prev;
        return [...prev, id];
      });
    } catch (err) {
      console.error(err);
      toast.error("Erreur serveur");
    }
  };

  const incrementView = async (videoId) => {
    try {
      await fetch(`${process.env.REACT_APP_APIURL}/video/view/increment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          video_id: videoId,
        }),
      });
    } catch (err) {
      console.error("view error", err);
    }
  };

  const sortedVideos = useMemo(() => {
    return (userData?.library_videos || []).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  }, [userData]);

  const totalPages = Math.ceil(sortedVideos.length / videosPerPage);

  const paginatedVideos = useMemo(() => {
    const start = (page - 1) * videosPerPage;
    return sortedVideos.slice(start, start + videosPerPage);
  }, [page, sortedVideos]);
  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: "#4318FF" }} />
      </Box>
    );

  return (
    <Box sx={{ py: 6, px: { xs: 2, md: 4 } }}>
      <Typography
        variant="h4"
        sx={{
          color: "var(--taupe-fonc, #2E2E2E)",
          textAlign: "center",
          fontFamily: "Lato",
          fontSize: "19px",
          fontStyle: "normal",
          fontWeight: 400,
          mb: "50px", // Maintient la marge extérieure
          pb: "20px", // Ajoute l'espace intérieur au bas
          width: {
            xs: "100%",
            sm: "70%",
          },
          margin: "0 auto",
        }}>
        Grâce au système d'achat de jetons, vos clients pourront accéder aux
        vidéos publiées par vos intervenants, qu'elles soient gratuites ou
        payantes.
      </Typography>

      <Grid container spacing={4}>
        {paginatedVideos.map((video) => {
          const ytId = getYouTubeId(video.file_name_lbrv);

          const videoId = normalizeId(video.id_video_lbrv);

          const isPaid = normalizeId(video.is_paid_video_lbrv) === 1;
          const isUnlocked = !isPaid || unlockedSet.has(videoId);
          const thumbnail = ytId
            ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`
            : video.thumbnail;
          const isYouTube = !!ytId;
          const isDirectVideo = !isYouTube && !!video.file_name_lbrv;

          const isFree = Number(video.is_paid_video_lbrv) === 0;
          const tokens = video.unlocks_tokens;
          return (
            <Grid item xs={12} sm={6} md={4} key={video.id_video_lbrv}>
              <Box
                onClick={() => handleAction(video)}
                sx={{
                  position: "relative",
                  borderRadius: "24px",
                  overflow: "hidden",
                  bgcolor: "#000",
                  aspectRatio: "16/9",
                  cursor: "pointer",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                  transition: "transform 0.3s ease",
                  "&:hover": { transform: "translateY(-5px)" },
                }}>
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    transition: "0.4s ease",
                    transform: isUnlocked ? "scale(1)" : "scale(1)",
                  }}>
                  {isDirectVideo ? (
                    <video
                      src={video.file_name_lbrv}
                      muted
                      playsInline
                      preload="metadata"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <img
                      src={thumbnail}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: isUnlocked ? "transparent" : "rgba(0,0,0,0.2)",
                  }}>
                  {isUnlocked ? (
                    <PlayArrowRounded sx={{ fontSize: 70, color: "#fff" }} />
                  ) : (
                    <Stack alignItems="center" spacing={1}>
                      <LockPerson sx={{ fontSize: 50, color: "#fff" }} />
                      <Typography color="#fff" variant="caption">
                        PREMIUM
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Box>
              <Stack direction="row" spacing={1} mt={1.5} alignItems="center">
                {/* Badge de Statut (Fixe) */}
                <Chip
                  label={isFree ? "Gratuit" : "Premium"}
                  size="small"
                  sx={{
                    bgcolor: isFree
                      ? "rgba(5, 205, 153, 0.1)"
                      : "rgba(255, 184, 0, 0.1)",
                    color: isFree ? "#05CD99" : "#FFB800",
                    fontWeight: 800,
                    fontSize: "0.65rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    borderRadius: "6px",
                    height: "22px",
                    border: isFree
                      ? "1px solid rgba(5, 205, 153, 0.2)"
                      : "1px solid rgba(255, 184, 0, 0.2)",
                  }}
                />

                {/* Badge de Prix (Uniquement si payant) */}
                {!isFree && (
                  <Chip
                    icon={
                      <Stars
                        sx={{ fontSize: "14px !important", color: "#4318FF" }}
                      />
                    }
                    label={`${tokens} jetons`}
                    size="small"
                    sx={{
                      bgcolor: "#F4F7FE", // Gris bleuté très pro
                      color: "#4318FF",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      borderRadius: "6px",
                      height: "22px",
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                )}
              </Stack>

              <Typography mt={2} fontWeight={800}>
                {video.name_video_lbrv}
              </Typography>
              <Typography variant="body2" sx={{ color: "#A3AED0" }}>
                {video.number_views_video_lbrv} vues •{" "}
                {formatRelativeTime(video.created_at)}
              </Typography>
            </Grid>
          );
        })}
      </Grid>
      <Box mt={5} display="flex" justifyContent="center">
  <Pagination 
    count={totalPages} 
    page={page} 
    onChange={(event, value) => setPage(value)} 
    color="primary"
    variant="outlined" 
    shape="rounded"
  />
</Box>
      {/* PLAYER */}
      <Dialog
        open={!!activeVideo}
        onClose={() => setActiveVideo(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#fff",
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0px 20px 40px rgba(0,0,0,0.2)",
          },
        }}>
        {/* ZONE VIDÉO */}
        <Box
          sx={{
            position: "relative",
            bgcolor: "#000",
            width: "100%",
            pt: "56.25%",
          }}>
          <IconButton
            onClick={() => setActiveVideo(null)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#fff",
              zIndex: 10,
              bgcolor: "rgba(0,0,0,0.5)",
              "&:hover": { bgcolor: "#000" },
            }}>
            <Close />
          </IconButton>

          <Box sx={{ position: "absolute", inset: 0 }}>
            {activeVideo?.isYouTube ? (
             <iframe
  title={activeVideo.title || "Lecteur vidéo"}
  width="100%"
  height="100%"
  src={activeVideo.embedUrl}
  style={{ border: 0 }}
  allow="autoplay; encrypted-media"
  allowFullScreen
/>

            ) : (
              <video
                src={activeVideo?.url || activeVideo?.file_name_lbrv}
                controls
                autoPlay
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            )}
          </Box>
        </Box>

        {/* ZONE DÉTAILS (FIXE) */}
        <DialogContent sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start">
              <Box sx={{ maxWidth: "70%" }}>
                <Typography
                  variant="h5"
                  fontWeight={900}
                  sx={{ color: "#1B2559", mb: 0.5, lineHeight: 1.2 }}>
                  {activeVideo?.title || activeVideo?.name_video_lbrv}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#A3AED0", fontWeight: 500 }}>
                  {activeVideo?.number_views_video_lbrv || 0} vues •{" "}
                  {formatRelativeTime(activeVideo?.created_at)}
                </Typography>
              </Box>

              {/* Statut et Jetons */}
              <Stack direction="row" spacing={1}>
                {Number(activeVideo?.is_paid_video_lbrv) !== 0 && (
                  <Chip
                    icon={
                      <Stars
                        sx={{ fontSize: "14px !important", color: "#4318FF" }}
                      />
                    }
                    label={`${activeVideo?.unlocks_tokens} jetons`}
                    size="small"
                    sx={{
                      bgcolor: "rgba(67, 24, 255, 0.05)",
                      fontWeight: 700,
                      color: "#4318FF",
                      borderRadius: "8px",
                      border: "1px solid rgba(67, 24, 255, 0.1)",
                    }}
                  />
                )}
                <Chip
                  label={
                    Number(activeVideo?.is_paid_video_lbrv) === 0
                      ? "Gratuit"
                      : "Débloqué"
                  }
                  size="small"
                  sx={{
                    bgcolor:
                      Number(activeVideo?.is_paid_video_lbrv) === 0
                        ? "rgba(5, 205, 153, 0.1)"
                        : "#F4F7FE",
                    fontWeight: 800,
                    color:
                      Number(activeVideo?.is_paid_video_lbrv) === 0
                        ? "#05CD99"
                        : "#707EAE",
                    borderRadius: "8px",
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                  }}
                />
              </Stack>
            </Stack>

            <Divider sx={{ opacity: 0.4 }} />

            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={800}
                sx={{
                  color: "#1B2559",
                  mb: 1,
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  letterSpacing: 1,
                }}>
                À propos de cette vidéo
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#707EAE",
                  lineHeight: 1.7,
                  maxHeight: "120px",
                  overflowY: "auto",
                  pr: 1,
                  "::-webkit-scrollbar": { width: "4px" },
                  "::-webkit-scrollbar-thumb": {
                    bgcolor: "#E0E5F2",
                    borderRadius: "10px",
                  },
                }}>
                {activeVideo?.description_video_lbrv ||
                  activeVideo?.description ||
                  "Aucune description fournie."}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={unlockModal.open}
        onClose={() => setUnlockModal({ open: false, video: null })}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            padding: "16px",
            maxWidth: "400px",
            width: "100%",
          },
        }}>
        <DialogContent>
          <Stack alignItems="center" spacing={3}>
            {/* Icône */}
            <Box
              sx={{
                bgcolor: "rgba(67, 24, 255, 0.08)",
                p: 3,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <LockPerson sx={{ fontSize: 60, color: "#4AAE9B" }} />
            </Box>

            {/* Titre */}
            <Stack alignItems="center" spacing={1}>
              <Typography
                variant="h5"
                fontWeight={900}
                sx={{ color: "#1B2559" }}>
                Vidéo Premium
              </Typography>
              <Typography
                variant="body1"
                textAlign="center"
                sx={{ color: "#707EAE", px: 2 }}>
                Cette vidéo est exclusive. Utilisez vos jetons pour débloquer
                l’accès.
              </Typography>
            </Stack>

            {/* Prix */}
            <Box
              sx={{
                bgcolor: "#F4F7FE",
                py: 1.5,
                px: 3,
                borderRadius: "12px",
                border: "1px dashed #4AAE9B",
              }}>
              <Typography fontWeight={700} sx={{ color: "#4318FF" }}>
                Prix : {unlockModal.video?.unlocks_tokens} Jetons
              </Typography>
            </Box>

            {/* Bouton */}
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                const video = unlockModal.video;
                if (!video) return;

                // 🔐 déjà unlock ?
                const alreadyUnlocked = clientData?.unlocked_videos?.some(
                  (v) => Number(v.video_id) === Number(video.id_video_lbrv),
                );

                if (alreadyUnlocked) {
                  toast.info("Vidéo déjà débloquée ✅");
                  setUnlockModal({ open: false, video: null });
                  return;
                }

                // 💰 tokens insuffisants
                if (clientData?.nbr_video < video.unlocks_tokens) {
                  toast.error("Solde de jetons insuffisant ❌");
                  return;
                }

                // 🚀 unlock API
                handleUnlockVideo(video.id_video_lbrv, video.unlocks_tokens);
                setUnlockModal({ open: false, video: null });
              }}
              sx={{
                bgcolor: "#4AAE9B",
                color: "#fff",
                py: 2,
                borderRadius: "16px",
                fontWeight: 700,
                fontSize: "1rem",
                textTransform: "none",
                boxShadow: "0px 10px 20px rgba(67, 24, 255, 0.2)",
                "&:hover": {
                  bgcolor: "#56c5b1",
                  boxShadow: "0px 12px 24px rgba(67, 24, 255, 0.3)",
                },
              }}>
              {(() => {
                const video = unlockModal.video;

                if (!video) return "Débloquer maintenant";

                const alreadyUnlocked = clientData?.unlocked_videos?.some(
                  (v) => Number(v.video_id) === Number(video.id_video_lbrv),
                );

                if (alreadyUnlocked) return "Déjà débloquée";

                if (clientData?.nbr_video < video.unlocks_tokens)
                  return "Jetons insuffisants";

                return "Débloquer maintenant";
              })()}
            </Button>

            {/* Annuler */}
            <Button
              onClick={() => setUnlockModal({ open: false, video: null })}
              sx={{
                color: "#A3AED0",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  bgcolor: "transparent",
                  color: "#1B2559",
                },
              }}>
              Peut-être plus tard
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={loginModal}
        onClose={() => setLoginModal(false)}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            p: 3,
            maxWidth: "420px",
            width: "100%",
          },
        }}>
        <DialogContent>
          <Stack alignItems="center" spacing={3}>
            <LockPerson sx={{ fontSize: 60, color: "#4AAE9B" }} />

            <Typography variant="h5" fontWeight={900}>
              Connexion requise
            </Typography>

            <Typography textAlign="center" sx={{ color: "#707EAE" }}>
              Vous devez être connecté pour accéder aux vidéos et débloquer le
              contenu premium.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                // 👉 adapte selon ton routing (React Router / Next / etc.)
                window.location.href = "/login";
              }}
              sx={{
                bgcolor: "#4AAE9B",
                borderRadius: "16px",
                py: 2,
                fontWeight: 700,
                textTransform: "none",
              }}>
              Se connecter
            </Button>

            <Button
              onClick={() => setLoginModal(false)}
              sx={{ color: "#A3AED0", textTransform: "none" }}>
              Annuler
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Videos;
